import { format } from "date-fns";
import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Button, Dropdown } from "react-bootstrap";
import { FaPencilAlt } from "react-icons/fa";
import { getUploadedFile } from "requesters/uploadsRequester";
import { getCurrentRole } from "../../../reducers";
import EditFileModal from "../modals/EditFileModal";
import RenameFileModal from "../modals/RenameFileModal";
import SendReleasedFilesEmailModal from "../modals/SendReleasedFilesEmailModal";
import DeleteFile from "./DeleteFile";
import SupersededFile from "./SupersededFile";
import { RECEIVED_PLANS_CATEGORY, PDF_MARKUP_ALLOWED_ROLES, FILE_GROUPS, ADMIN } from "../../../../../utils/constants";
import MarkFileAsUsedPlansForCalc from "./MarkFileAsUsedPlansForCalc";
import FileIcon from "./fileRow/FileIcon";
import PdfViewer from "../../../components/PdfViewer/PdfViewer";
import { toast } from "react-toastify"
import { extractApiErrorMessage, MAIN_TOAST_OPTIONS } from "utils/apiErrors"

export default function FileRow({
  file,
  fileGroup,
  fileGroupKey,
  jobService,
  onPdfExport,
  fileOptions = [],
  selectedFileIds = [],
  getCheckedFiles = () => {},
  reloadData = () => {},
  isDeleted = false,
  canUpdate = false,
  showSuperseded = false
}) {
  const {
    attributes: {
      createdByUser,
      childJobCode,
      fileTrackedUrl,
      fileName,
      documentCategory,
      isPlansUsedForCalc,
      updatedAt,
      name,
      discardedAt,
      discardedByUser,
      superseded,
      isExpired,
      extractMsg,
      readyForRelease
    }
  } = file.data

  const currentTeams = useSelector(getCurrentRole)
  const isAdmin = currentTeams.some(team => team.includes(ADMIN))
  const [showEditFiletModal, setShowEditFiletModal] = useState(false)
  const [showPdfViewer, setShowPdfViewer] = useState(false)
  const [showRenameFileModal, setShowRenameFileModal] = useState(false)
  const isJobInstructions = fileGroup === "Job Instructions"
  const isReadyForRelease = fileGroup === "Ready for Release"
  const isReleasedFiles = fileGroup === "Released Files"
  const isSearches = fileGroup === "Searches"
  const isPaidFull = jobService?.attributes?.invoiceState === "paid_full" || Number(jobService?.attributes?.cost) === 0
  const isAccounts = !(jobService?.fifty_fifty || jobService?.prior_to_release)
  const outstandingInvoiceFlagged = jobService?.attributes?.outstandingInvoiceFlagged
  const unmatchedInvoiceFlagged = jobService?.attributes?.unmatchedInvoiceFlagged
  const [showReleasedFilesModal, setShowReleasedFilesModal] = useState(false)
  const [loadingFileUrl, setLoadingFileUrl] = useState(false)
  const EXTRACT_MESSAGE = {
    extracting: {
      text: "Extracting file",
      color: "text-green"
    },
    error: {
      text: "Error extracting file",
      color: "text-danger"
    },
    unsupported: {
      text: "Unsupported file",
      color: "text-danger"
    }
  }
  const FILE_GROUP_LABEL_TO_KEY = {
    "Released Files": "released_files",
    "Ready for Release": "ready_for_release",
    "Registered Surveyor Files": "registered_surveyor_files",
    "Drafting Files": "drafting_files",
    "Flag Files": "flag_files",
    "Surveyor Files": "surveyor_files",
    "Job Instructions": "job_instructions",
    "Deleted Files": "deleted_files",
    Searches: "searches",
    Photos: "photos"
  }

  const canShowQuoteAcceptanceFiles = ["admin", "accounts", "operations", "3d_admin"].some(team => currentTeams.includes(team))
  if (isJobInstructions && !canShowQuoteAcceptanceFiles) {
    _.remove(fileOptions, o => o === "Quote Acceptance" || o === "Quote");
  }

  function attachAndDetachDownloadLink(downloadLink) {
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }

  // Check if file is PDF
  const isPdfFile = () => {
    const extension = fileName?.split(".").pop()?.toLowerCase()
    return extension === "pdf"
  }

  // Check if user has permission to use PDF markup feature for this specific file
  // Users can only markup PDFs from file categories covered by their role
  const canUserMarkupPdf = () => {
    // First check if user has a role that allows PDF markup
    const hasMarkupRole = PDF_MARKUP_ALLOWED_ROLES.some(role => currentTeams.some(team => team.includes(role)))
    if (!hasMarkupRole) return false

    // Admin/Operations can markup any file
    if (currentTeams.some(team => team.includes("admin") || team.includes("operations"))) {
      return true
    }

    // RS roles can markup RS files AND drafting files (they review drafting work before signing)
    if (currentTeams.some(team => team.includes("registered_surveyors"))) {
      if (FILE_GROUPS.registered_surveyor_files.includes(documentCategory)
          || FILE_GROUPS.drafting_files.includes(documentCategory)
          || FILE_GROUPS.vr_drafting_files.includes(documentCategory)) {
        return true
      }
    }

    // Drafting roles can only markup Drafting files
    if (currentTeams.some(team => team.includes("drafting") || team.includes("overseas_drafting"))) {
      if (FILE_GROUPS.drafting_files.includes(documentCategory)
          || FILE_GROUPS.vr_drafting_files.includes(documentCategory)) {
        return true
      }
    }

    return false
  }

  const showPdfMarkup = isPdfFile() && canUserMarkupPdf()

  // Direct download function (bypasses PDF viewer)
  async function handleDirectDownload() {
    setLoadingFileUrl(true)
    const result = await getUploadedFile(fileTrackedUrl)
    if ("response" in result) {
      const extension = result?.response?.json?.extension || ""
      const download_url = result?.response?.json?.download_url
      const file_name = result?.response?.json?.file_name
      const downloadLink = document.createElement("a");
      const isUrlFile = extension && extension.toLowerCase() === "url"

      if (isUrlFile) {
        fetch(download_url)
          .then(data => data.text())
          .then(content => {
            const lines = content?.split("\n") || []
            const urlLine = lines.find(l => l.toLowerCase().includes("url="))
            const url = urlLine?.split("=")?.pop()
            downloadLink.href = url;
            downloadLink.setAttribute("target", "_blank")
            attachAndDetachDownloadLink(downloadLink)
            setLoadingFileUrl(false)
          })
      } else if (extension && extension.toLowerCase() === "xml") {
        fetch(download_url)
          .then(data => data.blob())
          .then(blob => {
            const url = window.URL.createObjectURL(new Blob([blob]));
            downloadLink.href = url;
            downloadLink.setAttribute("download", file_name)
            attachAndDetachDownloadLink(downloadLink)
            setLoadingFileUrl(false)
          })
          .catch(err => {
            toast.error(`Could not download XML file: ${extractApiErrorMessage(err)}`, MAIN_TOAST_OPTIONS)
            setLoadingFileUrl(false)
          })
      } else {
        downloadLink.href = download_url;
        downloadLink.setAttribute("download", file_name)
        downloadLink.setAttribute("target", "_blank")
        attachAndDetachDownloadLink(downloadLink)
        setLoadingFileUrl(false)
      }
    } else {
      setLoadingFileUrl(false)
    }
  }

  const renderSendReleasedFilesEmailModal = useCallback(() => (
    <SendReleasedFilesEmailModal
      show={showReleasedFilesModal}
      onHide={() => setShowReleasedFilesModal(false)}
      jobService={jobService}
      file={file} />
  ), [jobService, showReleasedFilesModal, file])

  const isReceivedPlans = isJobInstructions && documentCategory === RECEIVED_PLANS_CATEGORY
  const isUsedPlans = isReceivedPlans && isPlansUsedForCalc

  // RS Checks files are always displayed as superseded (no restore option)
  const isRsChecksFile = documentCategory === "RS Checks"
  const displayAsSuperseded = superseded || isRsChecksFile
  const isSelected = selectedFileIds.includes(file.data.id)
  const showCheckbox = !(isReadyForRelease || isReleasedFiles) && !displayAsSuperseded
  const showCategoryEdit = canUpdate && !isDeleted && !fileGroup
  const showRenameItem = isAdmin
  const showEditMenu = showPdfMarkup || showRenameItem || showCategoryEdit
  const showDelete = canUpdate && (!isReleasedFiles || !file.data.attributes.releasedFileSentAt)
  const showSendToClient = isReleasedFiles && (isPaidFull || isAccounts)
  const formattedUpdatedAt = updatedAt ? format(new Date(updatedAt), "dd MMM yyyy, h:mm aa") : ""
  const formattedDiscardedAt = discardedAt ? format(new Date(discardedAt), "dd MMM yyyy, h:mm aa") : ""
  const cardClassName = [
    "file-card",
    isSelected ? "file-card--selected" : "",
    isSearches && isExpired ? "file-card--expired" : "",
    isUsedPlans ? "file-card--used-plans" : "",
    displayAsSuperseded ? "file-card--superseded" : ""
  ].filter(Boolean).join(" ")

  return (
    <>
      {isJobInstructions && !fileOptions.includes(documentCategory)
        ? <React.Fragment></React.Fragment>
        : (
          <tr className="file-card-row">
            <td colSpan={8} className="file-card-cell">
              <div className={cardClassName}>
                {showCheckbox && (
                  <div className="file-card__check custom-control custom-checkbox">
                    <input
                      className="custom-control-input"
                      id={`file-card-${file.data.id}`}
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => getCheckedFiles(file)} />
                    <label className="custom-control-label" htmlFor={`file-card-${file.data.id}`}></label>
                  </div>
                )}
                <div className="file-card__icon">
                  <FileIcon file={file} />
                </div>
                <div className="file-card__title">
                  <div className="file-card__title-line">
                    <Button
                      disabled={loadingFileUrl}
                      variant="link"
                      onClick={() => handleDirectDownload()}
                      title={name || fileName}
                      className="file-card__name">
                      {name || fileName}
                    </Button>
                    {showEditMenu && (
                      <Dropdown className="file-card__edit">
                        <Dropdown.Toggle
                          variant="link"
                          className="file-card__edit-toggle"
                          title="Edit file"
                          aria-label="Edit file">
                          <FaPencilAlt size={14} color="#007bff" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {showPdfMarkup && (
                            <Dropdown.Item as="button" onClick={() => setShowPdfViewer(true)}>
                              Edit PDF
                            </Dropdown.Item>
                          )}
                          {showRenameItem && (
                            <Dropdown.Item
                              as="button"
                              disabled={readyForRelease}
                              onClick={() => setShowRenameFileModal(true)}>
                              Rename File
                            </Dropdown.Item>
                          )}
                          {showCategoryEdit && (
                            <Dropdown.Item as="button" onClick={() => setShowEditFiletModal(true)}>
                              Edit
                            </Dropdown.Item>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    )}
                  </div>
                  {extractMsg && (
                    <i className={`file-card__extract ${EXTRACT_MESSAGE[extractMsg]?.color || ""}`}>
                      (
                      {EXTRACT_MESSAGE[extractMsg]?.text}
                      )
                    </i>
                  )}
                </div>
                <div className="file-card__meta">
                  <span className="file-card__meta-item">
                    {isReceivedPlans && (
                      <MarkFileAsUsedPlansForCalc
                        file={file}
                        jobService={jobService}
                        reloadData={reloadData}
                        isUsedPlans={isUsedPlans} />
                    )}
                    <i className="bi bi-file-earmark-text" aria-hidden="true"></i>
                    <span>
                      {isUsedPlans ? "Plans Used for Calculations" : documentCategory}
                      {isRsChecksFile && (
                        <span
                          className="ml-1"
                          title="RS Checks files are automatically superseded and cannot be restored">
                          (auto-superseded)
                        </span>
                      )}
                      {childJobCode && (
                        <span className="file-card__child-job">
                          {childJobCode}
                          {" "}
                          Job
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="file-card__sep" aria-hidden="true">|</span>
                  <span className="file-card__meta-item">
                    <i className="bi bi-person" aria-hidden="true"></i>
                    {createdByUser}
                  </span>
                  <span className="file-card__sep" aria-hidden="true">|</span>
                  <span className="file-card__meta-item">
                    <i className="bi bi-calendar" aria-hidden="true"></i>
                    {formattedUpdatedAt}
                  </span>
                  {isDeleted && (
                    <>
                      <span className="file-card__sep" aria-hidden="true">|</span>
                      <span className="file-card__meta-item">
                        <i className="bi bi-person-x" aria-hidden="true"></i>
                        {discardedByUser}
                      </span>
                      <span className="file-card__sep" aria-hidden="true">|</span>
                      <span className="file-card__meta-item">
                        <i className="bi bi-calendar-x" aria-hidden="true"></i>
                        {formattedDiscardedAt}
                      </span>
                    </>
                  )}
                </div>
                <div className="file-card__actions">
                  {showSendToClient && (
                    <button
                      disabled={outstandingInvoiceFlagged || unmatchedInvoiceFlagged}
                      type="button"
                      className="btn btn-link file-card__send"
                      onClick={() => setShowReleasedFilesModal(true)}>
                      {file.data.attributes.releasedFileSentAt ? "Resend" : "Send to Client"}
                    </button>
                  )}
                  {showSuperseded && !isRsChecksFile && (
                    <SupersededFile
                      reloadData={reloadData}
                      file={file} />
                  )}
                  {showSuperseded && isRsChecksFile && (
                    <span className="text-muted" title="RS Checks files cannot be restored">—</span>
                  )}
                  {showDelete && (
                    <DeleteFile
                      file={file}
                      reloadData={reloadData} />
                  )}
                </div>
              </div>
            </td>
          </tr>
        )
      }

      {showCategoryEdit && (
        <EditFileModal
          file={file}
          fileOptions={fileOptions}
          show={showEditFiletModal}
          reloadData={reloadData}
          onHide={() => { setShowEditFiletModal(false) }} />
      )}

      <RenameFileModal
        file={file}
        show={showRenameFileModal}
        reloadData={reloadData}
        onHide={() => setShowRenameFileModal(false)} />

      {showSendToClient && renderSendReleasedFilesEmailModal()}

      {/* PDF Viewer Modal */}
      {showPdfViewer && (
        <PdfViewer
          uploadedFileId={file?.data?.id}
          show={showPdfViewer}
          onHide={() => setShowPdfViewer(false)}
          title={name || fileName}
          jobService={jobService}
          assignment={null}
          onFlagCreated={reloadData}
          onUploadComplete={exportedFile => {
            setShowPdfViewer(false)
            if (onPdfExport) {
              // Pass normalized file group key so parent can set category options
              const normalizedFileGroup = fileGroupKey || FILE_GROUP_LABEL_TO_KEY[fileGroup] || null
              onPdfExport(exportedFile, normalizedFileGroup)
            }
          }} />
      )}
    </>
  )
}
